import deepmerge from 'deepmerge';
import useUrlReplace from '~/composables/useUrlReplace';
import useCachedFetch from '~/composables/useCachedFetch';
import { type ArticleTile } from '~/types/article';
import { type Collection } from '~/types/elements';
import { type ProviderConfig } from '~/types/system';

export default function useArticleStream () {
  const getArticles = async (provider: string, options?: Record<string, any>) => {
    const { replaceUrl } = useUrlReplace();
    const cacheKey = options?.key || `related-${provider}`;
    const providerConfig = useRuntimeConfig()?.public?.[provider] as ProviderConfig;
    const opt = deepmerge(providerConfig?.options || {}, options || {});
    const api = import.meta.client && providerConfig?.api ? providerConfig?.api : useRuntimeConfig()?.[provider]?.originApi;
    if (!api) {
      return [];
    }
    let size = opt.size || opt.limit || 10;

    let skipIds: string[] = Array.isArray(opt?.skipIds) ? opt.skipIds.filter((id): id is string => typeof id === 'string') : [];
    if (opt?.skipViewed) {
      const clickedArticles = await useItemVisited().filterItemByAction('clicked');
      const viewedArticles = await useItemVisited().filterItemByAction('viewed');
      const visitedArticles = Array.from(new Set([...clickedArticles, ...viewedArticles]));
      skipIds = [...new Set([...skipIds, ...visitedArticles])];
    }
    size = size + skipIds.length;
    const query = { size };
    if (opt.offset) {
      query.offset = opt.offset;
    }

    const localStorageCached = await useCachedFetch('stream', opt?.ttl || 0).getData(cacheKey, () => $fetch<Collection>(replaceUrl(api, opt), { query: query || {} }));
    const ids: { id: string, type: string }[] = localStorageCached.response?.items || [];
    const filteredArticleIds = skipIds.length ? ids.filter(({ id }) => !skipIds.includes(id)) : ids;
    const articleCachedFetch = useCachedFetch('article', useRuntimeConfig()?.public?.articleTeaser?.ttl);
    const articleRequests = filteredArticleIds.map(async (article) => {
      const { id } = article;
      const result = await articleCachedFetch.getData(`teaser-${id}`, async () => transformTeaser(await $fetch<ArticleTile>(useRuntimeConfig()?.public?.articleTeaser?.api.replace(':id', id))));
      return result?.response;
    });

    return {
      info: localStorageCached.response?.info,
      items: (await Promise.all(articleRequests) || []).filter(Boolean)
    };
  };

  return {
    getArticles
  };
}
